<script setup lang="ts">
    import { computed, ref } from 'vue';
    import { Scope, ScopeSelectorSelectionMode } from '@/shared/components/scopeselector/scopeSelectionHelper.ts';

    type Props = {
        scopes: Scope[];
        disabledScopes?: string[];
        selectionMode?: ScopeSelectorSelectionMode;
        enableRowSelection?: boolean;
        enablePinning?: boolean;
        hoverable?: boolean;
    };

    const props = withDefaults(defineProps<Props>(), {
        enableRowSelection: false,
        disabledScopes: () => [],
        selectionMode: () => ScopeSelectorSelectionMode.Multi,
        hoverable: () => false
    });

    const selectedScopes = defineModel<string[]>('selectedScopes', { required: true });
    const pinnedScopes = defineModel<string[]>('pinnedScopes', { required: false });
    const selectedScope = defineModel<string | null>('selectedScope', { required: false });

    const selectedScopeEntity = computed({
        get: () => {
            if (props.selectionMode === ScopeSelectorSelectionMode.Multi || selectedScope.value === null) {
                return null;
            } else {
                return props.scopes.find(scope => scope.identity === selectedScope.value) || null;
            }
        },
        set: (value: Scope | null) => {
            if (props.selectionMode === ScopeSelectorSelectionMode.Multi) {
                return;
            } else {
                selectedScope.value = value?.identity ?? null;
            }
        }
    });

    const search = ref<string>('');

    const filteredScopes = computed(() => {
        const searchFilter = search.value.toLowerCase();
        return props.scopes
            .filter((scope) =>
                scope.identity.concat(scope.description).toLowerCase().includes(searchFilter));
    });

    function clear() {
        selectedScopes.value = [];
        selectedScope.value = null;
    }

    function selectAll() {
        selectedScopes.value = props.scopes
            .map(scope => scope.identity)
            .filter(identity => !props.disabledScopes?.includes(identity));
    }

    function isPinned(scope: Scope): boolean {
        if (!pinnedScopes.value) {
            return false;
        }
        return pinnedScopes.value.includes(scope.identity);
    }
</script>

<template>
    <b-field>
        <div
            v-if="props.scopes.length > 1"
            class="is-flex is-align-items-center">
            <b-button
                class="mr-4"
                @click="clear()">
                Clear selected
            </b-button>
            <b-button
                v-if="selectionMode === ScopeSelectorSelectionMode.Multi"
                @click="selectAll()">
                Select all
            </b-button>
            <span
                v-if="enableRowSelection"
                class="ml-5">
                Click on a table row to select a facility
            </span>
        </div>
    </b-field>
    <b-field>
        <b-input
            v-if="props.scopes.length > 8"
            v-model="search"
            placeholder="Type to filter..."
            class="has-margin-bottom"
            icon-right="close-circle"
            icon-right-clickable
            @icon-right-click="search=''" />
    </b-field>
    <b-table
        v-model:selected="selectedScopeEntity"
        :hoverable="hoverable"
        :data="filteredScopes"
        sticky-header
        height="500px">
        <b-table-column>
            <template
                #default="slotProps">
                <b-checkbox-button
                    v-if="enablePinning"
                    v-model="pinnedScopes"
                    size="is-small"
                    type="is-transparent"
                    class="icon-only-checkbox"
                    :native-value="slotProps.row.identity"
                    @click.stop>
                    <i
                        class="mdi mdi-18px"
                        :class="isPinned(slotProps.row) ? 'mdi-pin pin-icon ' : 'mdi-pin-outline'"
                        :title="isPinned(slotProps.row) ? 'Unpin scope' : 'Pin scope'" />
                </b-checkbox-button>
                <b-checkbox
                    v-if="selectionMode === ScopeSelectorSelectionMode.Multi"
                    v-model="selectedScopes"
                    :disabled="disabledScopes?.includes(slotProps.row.identity)"
                    :native-value="slotProps.row.identity"
                    @click.stop />
            </template>
        </b-table-column>
        <b-table-column
            v-slot="table"
            field="Identity"
            label="Identity">
            {{ table.row.identity }}
        </b-table-column>
        <b-table-column
            v-slot="table"
            field="description"
            label="Description">
            {{ table.row.description }}
        </b-table-column>
    </b-table>
</template>
<style scoped>
.pin-icon {
    color: #FF1234;
}
.icon-only-checkbox:deep(.button) {
    border-color: transparent;
    padding: 0;
    background-color: transparent;
}
.icon-only-checkbox:deep(.button:hover) {
    border-radius: 20%;
    padding: 0;
    background-color: rgba(220, 220, 220, 1);
}
</style>
